import { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Upload, 
  Trash2, 
  Copy, 
  Check, 
  Loader2, 
  Image as ImageIcon,
  Search,
  X,
  Download,
  ZoomIn,
  Maximize2,
  Scissors
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const RESIZE_PRESETS = [
  { name: "thumbnail", width: 150, height: 150, label: "Küçük Resim (150x150)" },
  { name: "small", width: 300, height: 300, label: "Küçük (300x300)" },
  { name: "medium", width: 600, height: 600, label: "Orta (600x600)" },
  { name: "large", width: 1200, height: 1200, label: "Büyük (1200x1200)" },
  { name: "hero", width: 1920, height: 1080, label: "Hero Banner (1920x1080)" },
  { name: "card", width: 400, height: 300, label: "Kart (400x300)" },
  { name: "square", width: 500, height: 500, label: "Kare (500x500)" },
  { name: "banner", width: 1200, height: 400, label: "Banner (1200x400)" },
];

const AdminMediaGallery = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Resize state
  const [resizeDialog, setResizeDialog] = useState(null);
  const [resizeMode, setResizeMode] = useState('preset'); // 'preset' or 'custom'
  const [selectedPreset, setSelectedPreset] = useState('medium');
  const [customWidth, setCustomWidth] = useState(600);
  const [customHeight, setCustomHeight] = useState(600);
  const [maintainAspect, setMaintainAspect] = useState(true);
  const [quality, setQuality] = useState(85);
  const [resizing, setResizing] = useState(false);
  const [imageInfo, setImageInfo] = useState(null);

  const fetchFiles = async () => {
    try {
      const response = await api.get('/admin/uploads');
      setFiles(response.data.files || []);
    } catch (error) {
      console.error('Error fetching files:', error);
      toast.error('Dosyalar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, []);

  const onDrop = useCallback(async (acceptedFiles) => {
    setUploading(true);
    let successCount = 0;

    for (const file of acceptedFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        await api.post('/admin/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        successCount++;
      } catch (error) {
        toast.error(`${file.name} yüklenemedi: ${error.response?.data?.detail || 'Hata'}`);
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} dosya başarıyla yüklendi`);
      fetchFiles();
    }
    setUploading(false);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp', '.svg']
    },
    disabled: uploading
  });

  const handleDelete = async (filename) => {
    try {
      await api.delete(`/admin/uploads/${filename}`);
      toast.success('Dosya silindi');
      setFiles(files.filter(f => f.filename !== filename));
      setDeleteConfirm(null);
    } catch (error) {
      toast.error('Dosya silinirken hata oluştu');
    }
  };

  const openResizeDialog = async (file) => {
    setResizeDialog(file);
    setResizeMode('preset');
    setSelectedPreset('medium');
    setMaintainAspect(true);
    setQuality(85);
    
    // Get image info
    try {
      const response = await api.get(`/admin/uploads/${file.filename}/info`);
      setImageInfo(response.data);
      setCustomWidth(response.data.width || 600);
      setCustomHeight(response.data.height || 600);
    } catch (error) {
      console.error('Error fetching image info:', error);
    }
  };

  const handleResize = async () => {
    if (!resizeDialog) return;
    
    setResizing(true);
    try {
      const formData = new FormData();
      
      if (resizeMode === 'preset') {
        formData.append('preset', selectedPreset);
      } else {
        formData.append('width', customWidth);
        formData.append('height', customHeight);
      }
      formData.append('maintain_aspect', maintainAspect);
      formData.append('quality', quality);
      
      const response = await api.post(`/admin/uploads/${resizeDialog.filename}/resize`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      toast.success(`Görsel boyutlandırıldı: ${response.data.new_size.width}x${response.data.new_size.height}`);
      setResizeDialog(null);
      setImageInfo(null);
      fetchFiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Boyutlandırma başarısız');
    } finally {
      setResizing(false);
    }
  };

  const copyToClipboard = async (url) => {
    const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${url}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopiedUrl(url);
      toast.success('URL kopyalandı!');
      setTimeout(() => setCopiedUrl(null), 2000);
    } catch (error) {
      toast.error('Kopyalama başarısız');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredFiles = files.filter(file => 
    file.filename.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-media-gallery">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Medya Galerisi
            </h2>
            <p className="text-slate-600">
              Yüklenen görselleri yönetin • {files.length} dosya
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Dosya ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-64"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Upload Area */}
        <div
          {...getRootProps()}
          className={`
            border-2 border-dashed rounded-xl p-8 transition-all cursor-pointer
            ${isDragActive ? 'border-[#005CB2] bg-[#005CB2]/5' : 'border-slate-300 hover:border-[#005CB2]/50'}
            ${uploading ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center gap-3">
            {uploading ? (
              <>
                <Loader2 className="w-10 h-10 text-[#005CB2] animate-spin" />
                <p className="text-slate-600 font-medium">Yükleniyor...</p>
              </>
            ) : (
              <>
                <div className="w-16 h-16 bg-[#005CB2]/10 rounded-full flex items-center justify-center">
                  <Upload className="w-8 h-8 text-[#005CB2]" />
                </div>
                <div className="text-center">
                  <p className="text-slate-700 font-medium">
                    {isDragActive ? 'Dosyaları bırakın' : 'Görselleri sürükleyin veya tıklayın'}
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    JPG, PNG, GIF, WebP, SVG • Max 5MB • Birden fazla dosya yükleyebilirsiniz
                  </p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Files Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#005CB2] animate-spin" />
          </div>
        ) : filteredFiles.length === 0 ? (
          <div className="text-center py-20">
            <ImageIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">
              {searchTerm ? 'Aramanızla eşleşen dosya bulunamadı' : 'Henüz yüklenmiş görsel yok'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filteredFiles.map((file) => (
              <Card 
                key={file.filename} 
                className="group overflow-hidden border-slate-200 hover:border-[#005CB2]/30 transition-all hover:shadow-md"
              >
                <CardContent className="p-0">
                  {/* Image Preview */}
                  <div 
                    className="aspect-square relative bg-slate-100 cursor-pointer"
                    onClick={() => setSelectedFile(file)}
                  >
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${file.url}`}
                      alt={file.filename}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    {/* Overlay on hover */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        className="h-8 w-8 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFile(file);
                        }}
                        title="Önizle"
                      >
                        <ZoomIn className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        className="h-8 w-8 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          openResizeDialog(file);
                        }}
                        title="Boyutlandır"
                      >
                        <Scissors className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        className="h-8 w-8 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(file.url);
                        }}
                        title="URL Kopyala"
                      >
                        {copiedUrl === file.url ? (
                          <Check className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        className="h-8 w-8 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirm(file);
                        }}
                        title="Sil"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* File Info */}
                  <div className="p-3">
                    <p className="text-xs text-slate-600 truncate font-medium" title={file.filename}>
                      {file.filename.substring(0, 20)}...
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Preview Dialog */}
        <Dialog open={!!selectedFile} onOpenChange={() => setSelectedFile(null)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold truncate">
                {selectedFile?.filename}
              </DialogTitle>
            </DialogHeader>
            
            {selectedFile && (
              <div className="space-y-4">
                <div className="bg-slate-100 rounded-lg overflow-hidden">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${selectedFile.url}`}
                    alt={selectedFile.filename}
                    className="w-full max-h-[60vh] object-contain"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Boyut</p>
                    <p className="font-medium">{formatFileSize(selectedFile.size)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Yüklenme Tarihi</p>
                    <p className="font-medium">{formatDate(selectedFile.created_at)}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-slate-500 mb-1">URL</p>
                    <div className="flex gap-2">
                      <Input
                        value={`${process.env.REACT_APP_BACKEND_URL}${selectedFile.url}`}
                        readOnly
                        className="text-xs"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(selectedFile.url)}
                      >
                        {copiedUrl === selectedFile.url ? (
                          <Check className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setSelectedFile(null)}>
                Kapat
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  setDeleteConfirm(selectedFile);
                  setSelectedFile(null);
                }}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Sil
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Dosyayı Sil</DialogTitle>
            </DialogHeader>
            <p className="text-slate-600">
              <strong>{deleteConfirm?.filename}</strong> dosyasını silmek istediğinizden emin misiniz?
              Bu işlem geri alınamaz.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
                İptal
              </Button>
              <Button
                variant="destructive"
                onClick={() => handleDelete(deleteConfirm?.filename)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Sil
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Resize Dialog */}
        <Dialog open={!!resizeDialog} onOpenChange={() => { setResizeDialog(null); setImageInfo(null); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Scissors className="w-5 h-5 text-[#005CB2]" />
                Görsel Boyutlandır
              </DialogTitle>
            </DialogHeader>
            
            {resizeDialog && (
              <div className="space-y-6">
                {/* Preview */}
                <div className="bg-slate-100 rounded-lg p-4 flex items-center gap-4">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${resizeDialog.url}`}
                    alt={resizeDialog.filename}
                    className="w-20 h-20 object-cover rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{resizeDialog.filename}</p>
                    {imageInfo && (
                      <p className="text-xs text-slate-500 mt-1">
                        Mevcut boyut: {imageInfo.width} x {imageInfo.height} px
                      </p>
                    )}
                    <p className="text-xs text-slate-500">
                      {formatFileSize(resizeDialog.size)}
                    </p>
                  </div>
                </div>

                {/* Mode Selection */}
                <div className="space-y-3">
                  <Label>Boyutlandırma Modu</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={resizeMode === 'preset' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setResizeMode('preset')}
                      className={resizeMode === 'preset' ? 'bg-[#005CB2]' : ''}
                    >
                      Hazır Boyutlar
                    </Button>
                    <Button
                      type="button"
                      variant={resizeMode === 'custom' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setResizeMode('custom')}
                      className={resizeMode === 'custom' ? 'bg-[#005CB2]' : ''}
                    >
                      Özel Boyut
                    </Button>
                  </div>
                </div>

                {/* Preset Selection */}
                {resizeMode === 'preset' && (
                  <div className="space-y-3">
                    <Label>Hazır Boyut Seçin</Label>
                    <Select value={selectedPreset} onValueChange={setSelectedPreset}>
                      <SelectTrigger>
                        <SelectValue placeholder="Boyut seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        {RESIZE_PRESETS.map((preset) => (
                          <SelectItem key={preset.name} value={preset.name}>
                            {preset.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Custom Size */}
                {resizeMode === 'custom' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Genişlik (px)</Label>
                        <Input
                          type="number"
                          value={customWidth}
                          onChange={(e) => setCustomWidth(parseInt(e.target.value) || 0)}
                          min={1}
                          max={4000}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Yükseklik (px)</Label>
                        <Input
                          type="number"
                          value={customHeight}
                          onChange={(e) => setCustomHeight(parseInt(e.target.value) || 0)}
                          min={1}
                          max={4000}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Options */}
                <div className="space-y-4 border-t pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>En-Boy Oranını Koru</Label>
                      <p className="text-xs text-slate-500">Görsel oranını bozmadan boyutlandırır</p>
                    </div>
                    <Switch
                      checked={maintainAspect}
                      onCheckedChange={setMaintainAspect}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Kalite: {quality}%</Label>
                    </div>
                    <Slider
                      value={[quality]}
                      onValueChange={(value) => setQuality(value[0])}
                      min={10}
                      max={100}
                      step={5}
                      className="py-2"
                    />
                    <p className="text-xs text-slate-500">
                      Düşük kalite = küçük dosya, yüksek kalite = büyük dosya
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => { setResizeDialog(null); setImageInfo(null); }}>
                İptal
              </Button>
              <Button
                onClick={handleResize}
                disabled={resizing}
                className="bg-[#005CB2] hover:bg-[#004a8f]"
              >
                {resizing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    İşleniyor...
                  </>
                ) : (
                  <>
                    <Scissors className="w-4 h-4 mr-2" />
                    Boyutlandır
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminMediaGallery;
